import React, { useState } from 'react';

const HowItWorksPage = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  
  // Simple navigation function using window.location
  const navigateTo = (path) => {
    window.location.href = path;
  };

  const videoSections = [
    {
      id: 'individual-calculator',
      title: '1. What\'s It All About?',
      description: 'Master individual buy-to-let property calculations',
      duration: '2:38',
      category: 'individual',
      topics: [
        'Entering property details accurately',
        'Setting purchase parameters',
        'Understanding rental yields',
        'Interpreting calculation results'
      ]
    },
    {
      id: 'getting-started',
      title: '2. Getting Started',
      description: 'Learn the basics of professional property investment analysis',
      duration: '1:08',
      category: 'basics',
      topics: [
        'How to choose the right calculator',
        'What you need to input',
        'What\'s next'
      ]
    },
    {
      id: 'spv-calculator',
      title: '3. Interpreting Results',
      description: 'Advanced property investment through company structures',
      duration: '2:07',
      category: 'spv',
      topics: [
        'Understanding SPV structures',
        'Corporation tax implications',
        'Setting up SPV parameters',
        'Comparing SPV vs Individual returns'
      ]
    },
    {
      id: 'advanced-features',
      title: '4. Sorting & Filtering Results',
      description: 'Professional tools for mortgage intermediaries',
      duration: '4:30',
      category: 'advanced',
      topics: [
        'Custom scenario modeling',
        'Stress testing calculations',
        'Professional report generation',
        'Client presentation tools'
      ]
    }
  ];

  const VideoModal = ({ video, onClose }) => {
    const [showSignUpButton, setShowSignUpButton] = useState(false);
    const [videoLoading, setVideoLoading] = useState(true);
    const [videoError, setVideoError] = useState(false);

    // Auto-play video when modal opens and video is ready
    React.useEffect(() => {
      if (video) {
        setVideoLoading(true);
        setVideoError(false);
        
        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
          const videoElement = document.querySelector('.modal-video');
          if (videoElement) {
            const handleCanPlay = () => {
              setVideoLoading(false);
              // Small delay before playing to ensure readiness
              setTimeout(() => {
                videoElement.play().catch(err => {
                  console.log('Autoplay failed:', err);
                  setVideoLoading(false);
                });
              }, 100);
            };
            
            const handleError = () => {
              setVideoError(true);
              setVideoLoading(false);
            };
            
            const handleLoadStart = () => {
              setVideoLoading(true);
            };

            videoElement.addEventListener('canplay', handleCanPlay);
            videoElement.addEventListener('error', handleError);
            videoElement.addEventListener('loadstart', handleLoadStart);
            
            // Force load the video
            videoElement.load();
            
            return () => {
              videoElement.removeEventListener('canplay', handleCanPlay);
              videoElement.removeEventListener('error', handleError);
              videoElement.removeEventListener('loadstart', handleLoadStart);
            };
          }
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }, [video]);

    // Show button at specific times for each video
    React.useEffect(() => {
      if (video && !videoLoading) {
        const videoElement = document.querySelector('.modal-video');
        
        const handleTimeUpdate = () => {
          let triggerTime = 0;
          
          switch (video.id) {
            case 'individual-calculator':
              triggerTime = 150; // 2:30
              break;
            case 'getting-started':
              triggerTime = 60; // 1:00
              break;
            case 'spv-calculator':
              triggerTime = 119; // 1:59
              break;
            case 'advanced-features':
              triggerTime = 262; // 4:22
              break;
            default:
              triggerTime = 0;
          }
          
          if (videoElement.currentTime >= triggerTime && triggerTime > 0) {
            setShowSignUpButton(true);
          }
        };

        if (videoElement) {
          videoElement.addEventListener('timeupdate', handleTimeUpdate);
          
          return () => {
            videoElement.removeEventListener('timeupdate', handleTimeUpdate);
          };
        }
      }
    }, [video, videoLoading]);

    if (!video) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '10px',
          border: '5px solid black',
          padding: '30px',
          maxWidth: '1000px',
          width: '100%',
          maxHeight: '95vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.4)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px'
          }}>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#157DEC',
                margin: '0 0 8px 0',
                fontFamily: 'Century Gothic, sans-serif'
              }}>
                {video.title}
              </h3>
            </div>
            
            {showSignUpButton && !videoLoading && (
              <button
                onClick={() => {
                  window.open('/calculator-suite', '_blank');
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  background: '#157DEC',
                  color: 'white',
                  border: '2px solid black',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  marginRight: '10px'
                }}
              >
                Sign Up Now
              </button>
            )}
            
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '30px',
                cursor: 'pointer',
                color: '#4a5568',
                padding: '0'
              }}
            >
              ×
            </button>
          </div>
          
          {/* Video Content with Loading Overlay */}
          <div style={{
            width: '100%',
            height: '400px',
            borderRadius: '8px',
            border: '3px solid black',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Loading Overlay */}
            {videoLoading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(21, 125, 236, 0.9)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                color: 'white',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid rgba(255,255,255,0.3)',
                  borderTop: '4px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '15px'
                }}></div>
                Loading Video...
              </div>
            )}
            
            {/* Error State */}
            {videoError && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(220, 53, 69, 0.9)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                color: 'white',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                Error loading video. Please try again.
              </div>
            )}

            {(() => {
              if (video.id === 'individual-calculator') {
                return (
                  <video 
                    className="modal-video"
                    controls 
                    preload="auto"
                    playsInline
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  >
                   <source src="/BTLCC WIAA FINAL with ST 09 09 25.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                );
              } else if (video.id === 'getting-started') {
                return (
                  <video 
                    className="modal-video"
                    controls 
                    preload="auto"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  >
                    <source src="/NEW GS FINAL 26 09 25.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                );
              } else if (video.id === 'spv-calculator') {
                return (
                  <video 
                    className="modal-video"
                    controls 
                    preload="auto"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  >
                    <source src="/Video 3 26 09 25.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                );
              } else if (video.id === 'advanced-features') {
                return (
                  <video 
                    className="modal-video"
                    controls 
                    preload="auto"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  >
                    <source src="/Video 4 26 09 25.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                );
              } else {
                return (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#f7fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ textAlign: 'center', color: '#4a5568' }}>
                      <div style={{ fontSize: '60px', marginBottom: '10px' }}>▶</div>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>ClipChamp Video Player</p>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>Duration: {video.duration}</p>
                    </div>
                  </div>
                );
              }
            })()}
          </div>
        </div>
        
        {/* Add CSS for spinner animation */}
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  };

  return (
 <div style={{
  height: '100vh',
  background: 'linear-gradient(135deg, #157DEC 0%, #1054a8 100%)',
  padding: '15px',
  fontFamily: 'Century Gothic, sans-serif',
  overflow: 'hidden'
}}>
      <div style={{
  maxWidth: '1200px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  height: 'calc(100vh - 30px)',
  overflow: 'hidden'
}}>
        
        {/* Header Section - Logo Left, Back Button Right */}
        <div style={{
          background: 'white',
          padding: '20px 35px',
          borderRadius: '10px',
          border: '5px solid black',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            {/* Left Side - BTL Calculator Logo */}
            <div style={{ 
              width: '280px',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center'
            }}>
              <svg 
                viewBox="0 0 360 90" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ 
                  width: '280px', 
                  height: '70px'
                }}
              >
                <defs>
                  <linearGradient id="brandBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#157DEC', stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#1054a8', stopOpacity:1}} />
                  </linearGradient>
                  <linearGradient id="accentBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#1054a8', stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#0c4389', stopOpacity:1}} />
                  </linearGradient>
                  <linearGradient id="lightBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#3b94f0', stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#157DEC', stopOpacity:1}} />
                  </linearGradient>
                  
                  <filter id="energyGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                  
                  <filter id="sharpShadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="3" dy="3" stdDeviation="0" floodColor="rgba(0,0,0,0.25)"/>
                  </filter>
                </defs>
                
                <path d="M 15 15 L 65 25 L 55 65 L 25 55 Z" fill="url(#brandBlue)" opacity="0.08" transform="rotate(12 40 40)"/>
                <circle cx="50" cy="50" r="25" fill="url(#lightBlue)" opacity="0.05"/>
                
                <g transform="translate(20, 22) skewX(-8)" filter="url(#sharpShadow)">
                  <rect x="6" y="16" width="26" height="20" fill="url(#brandBlue)" rx="3"/>
                  <path d="M 19 6 L 4 16 L 34 16 Z" fill="url(#accentBlue)"/>
                  <rect x="12" y="26" width="7" height="10" fill="#ffffff" rx="2"/>
                  <circle cx="17" cy="31" r="1.2" fill="url(#lightBlue)"/>
                  <rect x="22" y="20" width="7" height="5" fill="#ffffff" rx="1"/>
                  <rect x="8" y="20" width="4" height="4" fill="#ffffff" rx="1"/>
                  <path d="M 19 8 L 15 12 L 23 12 Z" fill="#ffffff" opacity="0.9"/>
                </g>
                
                <g transform="translate(50, 28) skewX(5)" filter="url(#energyGlow)">
                  <rect x="0" y="0" width="20" height="26" fill="url(#lightBlue)" rx="4"/>
                  <rect x="2" y="2" width="16" height="7" fill="#000000" rx="2"/>
                  <text x="10" y="7" textAnchor="middle" fill="#157DEC" fontFamily="JetBrains Mono, monospace" fontSize="3.5" fontWeight="700">+18.2%</text>
                  
                  <g fill="#ffffff">
                    <rect x="3" y="11" width="3" height="2.5" rx="0.5"/>
                    <rect x="7" y="11" width="3" height="2.5" rx="0.5"/>
                    <rect x="11" y="11" width="3" height="2.5" rx="0.5"/>
                    <rect x="15" y="11" width="3" height="2.5" rx="0.5" fill="url(#accentBlue)"/>
                    
                    <rect x="3" y="14.5" width="3" height="2.5" rx="0.5"/>
                    <rect x="7" y="14.5" width="3" height="2.5" rx="0.5"/>
                    <rect x="11" y="14.5" width="3" height="2.5" rx="0.5"/>
                    <rect x="15" y="14.5" width="3" height="2.5" rx="0.5" fill="url(#accentBlue)"/>
                    
                    <rect x="3" y="18" width="3" height="2.5" rx="0.5"/>
                    <rect x="7" y="18" width="3" height="2.5" rx="0.5"/>
                    <rect x="11" y="18" width="3" height="2.5" rx="0.5"/>
                    <rect x="15" y="18" width="3" height="5" rx="0.5" fill="url(#brandBlue)"/>
                    
                    <rect x="3" y="21.5" width="7" height="2.5" rx="0.5"/>
                    <rect x="11" y="21.5" width="3" height="2.5" rx="0.5"/>
                  </g>
                  
                  <circle cx="17" cy="3.5" r="1" fill="url(#brandBlue)" opacity="0.9"/>
                </g>
                
                <g transform="translate(-5, 0)">
                  <g transform="translate(95, 0)">
                    <text x="0" y="22" fontFamily="Poppins, system-ui, sans-serif" fontSize="20" fontWeight="800" fill="#1f2937">
                      <tspan fill="url(#brandBlue)">The Buy</tspan><tspan fill="url(#brandBlue)"> to </tspan><tspan fill="url(#brandBlue)">Let</tspan>
                    </text>
                    <text x="0" y="44" fontFamily="Poppins, system-ui, sans-serif" fontSize="20" fontWeight="800" fill="#1f2937">
                      Calculator <tspan fill="url(#brandBlue)">Company</tspan>
                    </text>
                    
                    <text x="0" y="63" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="500" fontStyle="italic" fill="url(#brandBlue)" letterSpacing="0.5px">
                      Affordability Guidance for Brokers
                    </text>
                    
                    <rect x="0" y="68" width="60" height="2" fill="url(#brandBlue)" rx="1" opacity="0.7"/>
                  </g>
                </g>
                
                <g opacity="0.6">
                  <path d="M 320 20 L 330 15 L 340 25 L 325 30 Z" fill="url(#accentBlue)"/>
                  <circle cx="315" cy="60" r="4" fill="url(#lightBlue)"/>
                  <rect x="330" y="70" width="8" height="8" fill="url(#brandBlue)" rx="2" transform="rotate(25 334 74)"/>
                </g>
                
                <path d="M 280 30 Q 290 25 300 30" stroke="url(#brandBlue)" strokeWidth="1.5" fill="none" opacity="0.4"/>
                <path d="M 285 65 Q 295 60 305 65" stroke="url(#accentBlue)" strokeWidth="1" fill="none" opacity="0.5"/>
              </svg>
            </div>

            {/* Center - Title */}
            <h1 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#157DEC',
              margin: 0,
              flex: 1,
              textAlign: 'center'
            }}>
              How the Buy to Let Calculator Works
            </h1>

            {/* Right Side - Back Button */}
            <div style={{ 
              width: '280px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}>
              <button
                onClick={() => navigateTo('/')}
                style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  background: 'white',
                  color: '#157DEC',
                  border: '2px solid black',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ← Back to Home
              </button>
            </div>
          </div>
          <p style={{
            fontSize: '18px',
            color: '#4a5568',
            fontWeight: 'bold',
            margin: 0
          }}>
            Watch the Videos Below to Learn All You Need to Know About Using the BTL Calculator
          </p>
        </div>

        {/* Video Grid - Two Rows of Two */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '15px'
        }}>
          {videoSections.map((video, index) => (
            <div 
              key={video.id}
              style={{
                background: '#157DEC',
                padding: '15px 20px',
                borderRadius: '8px',
                border: '3px solid white',
                boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.2)';
              }}
              onClick={() => setActiveVideo(video)}
            >
              <h3 style={{
                fontSize: '26px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '10px',
                textAlign: 'center',
                textShadow: '2px 2px 0px black, -2px -2px 0px black, 2px -2px 0px black, -2px 2px 0px black'
              }}>
                {video.title}
              </h3>

              <p style={{
                fontSize: '14px',
                color: 'white',
                cursor: 'pointer',
                margin: 0
              }}>
                Click to Watch Video ({video.duration})
              </p>
            </div>
          ))}
        </div>

        {/* Getting Started Section */}
        <div style={{
          background: 'white',
          padding: '20px 35px 15px 35px',
          borderRadius: '10px',
          border: '5px solid black',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#157DEC',
            margin: '0 0 8px 0'
          }}>
            Ready to Get Started?
          </h3>
          <p style={{
            fontSize: '16px',
            color: '#4a5568',
            margin: '0 0 12px 0',
            fontWeight: 'bold'
          }}>
            Watch the Videos Above and Click on Sign Up Now to Get Started
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            margin: 0
          }}>
            <button
              onClick={() => setActiveVideo(videoSections[0])}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                background: '#157DEC',
                color: 'white',
                border: '2px solid black',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Start with Video 1
            </button>
            <button
              onClick={() => navigateTo('/calculator-suite')}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                background: 'white',
                color: '#157DEC',
                border: '2px solid black',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Sign Up Now
            </button>
            <button
              onClick={() => navigateTo('/')}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                background: 'white',
                color: '#157DEC',
                border: '2px solid black',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Back to Home
            </button>
          </div>
        </div>

        {/* Footer Section */}
        <div style={{
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            marginBottom: '10px'
          }}>
            <a href="/Privacy Policy (August 2025).pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'underline', fontSize: '0.85rem' }}>Privacy Policy</a>
            <a href="/Terms and Conditions (August 2025).pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'underline', fontSize: '0.85rem' }}>T's and C's</a>
            <a href="/Cookies Policy (August 2025).pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'underline', fontSize: '0.85rem' }}>Cookies Policy</a>
            <a href="https://wa.me/447874401497" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'underline', fontSize: '0.85rem' }}>Contact</a>
            <a href="mailto:support@btlcc.co.uk" style={{ color: 'white', textDecoration: 'underline', fontSize: '0.85rem' }}>Support</a>
          </div>
          <p style={{
            color: 'white',
            fontSize: '0.8rem',
            margin: '0 0 3px 0'
          }}>
            © 2025 The Buy to Let Calculator Company Ltd. All rights reserved.
          </p>
          <p style={{
            color: 'white',
            fontSize: '0.65rem',
            margin: 0,
            whiteSpace: 'nowrap'
          }}>
            The Buy To Let Calculator Company Ltd Act on a Non-Advisory, Information-Only Basis and The Calculators do not Provide any Financial Advice
          </p>
        </div>

      </div>

      {/* Video Modal */}
      <VideoModal 
        video={activeVideo} 
        onClose={() => setActiveVideo(null)} 
      />
    </div>
  );
};

export default HowItWorksPage;